import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  InputLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ExerciseChart, { ExerciseChartDataPoint } from "./index";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";

const styleInputDefault = {
  fontSize: convertPxToRem(16),
  fontWeight: 600,
  color: theme.palette.neutral?.[900] || theme.palette.grey[900],
  padding: `${convertPxToRem(10)} ${convertPxToRem(14)}`,
};

// Data matching reference: first point ~150 (amber), rest 300+ (green)
const initialData: ExerciseChartDataPoint[] = [
  {
    date: "20/01",
    minutesPerWeek: 150,
    fullDate: "20.01.2025",
  },
  {
    date: "02/04",
    minutesPerWeek: 320,
    fullDate: "02.04.2025",
  },
  {
    date: "04/04",
    minutesPerWeek: 350,
    fullDate: "04.04.2025",
  },
  {
    date: "12/05",
    minutesPerWeek: 310,
    fullDate: "12.05.2025",
  },
  {
    date: "20/08",
    minutesPerWeek: 380,
    fullDate: "20.08.2025",
    isSelected: true,
  },
];

const themeColors = {
  error: theme.palette.error.main || "#d32f2f",
  warning: theme.palette.secondary[900] || "#f57c00",
  success: theme.palette.accent1[700] || "#1b5e20",
};

export default function ExerciseChartExamples() {
  const [data, setData] = useState<ExerciseChartDataPoint[]>(initialData);
  const [newMinutes, setNewMinutes] = useState<string>("");
  const [entriesAdded, setEntriesAdded] = useState<number>(0);
  const MAX_NEW_ENTRIES = 2;

  const handleMinutesInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setNewMinutes(value);
    }
  };

  const handleAddDataPoint = () => {
    if (entriesAdded >= MAX_NEW_ENTRIES) return;

    const minutes = parseInt(newMinutes, 10);
    if (isNaN(minutes) || minutes < 0) return;

    const now = new Date();
    const newPoint: ExerciseChartDataPoint = {
      date: `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}`,
      minutesPerWeek: minutes,
      fullDate: `${String(now.getDate()).padStart(2, "0")}.${String(now.getMonth() + 1).padStart(2, "0")}.${now.getFullYear()}`,
      isSelected: true,
    };

    const updatedData = data.map((point) => ({
      ...point,
      isSelected: false,
    }));
    setData([...updatedData, newPoint]);
    setEntriesAdded(entriesAdded + 1);
    setNewMinutes("");
  };

  return (
    <>
      <Box sx={{ padding: 2, marginBottom: 1 }}>
        <ExerciseChart data={data} />
      </Box>

      <Box sx={{ padding: 2, marginBottom: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 1,
            color: theme.palette.text.primary,
          }}
        >
          Add New Exercise Reading
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            marginBottom: 2,
            color: theme.palette.grey[600],
          }}
        >
          You can add {MAX_NEW_ENTRIES - entriesAdded} more reading
          {MAX_NEW_ENTRIES - entriesAdded !== 1 ? "s" : ""}
        </Typography>

        <Stack direction="row" gap={1} alignItems="flex-end" flexWrap="wrap">
          <Stack spacing={1} sx={{ flex: 1 }}>
            <InputLabel htmlFor="exercise-minutes-input">
              <Typography color="neutral.700" variant="body2" fontWeight={300}>
                Minutes per week
              </Typography>
            </InputLabel>
            <TextField
              id="exercise-minutes-input"
              value={newMinutes}
              size="small"
              onChange={handleMinutesInput}
              placeholder="200"
              disabled={entriesAdded >= MAX_NEW_ENTRIES}
              slotProps={{
                htmlInput: {
                  autoComplete: "off",
                  "aria-label": "Minutes per week",
                  inputMode: "numeric",
                },
              }}
              sx={{
                input: {
                  ...styleInputDefault,
                  fontSize: convertPxToRem(16),
                },
              }}
              fullWidth
            />
          </Stack>

          <Button
            variant="contained"
            onClick={handleAddDataPoint}
            disabled={!newMinutes || entriesAdded >= MAX_NEW_ENTRIES}
            sx={{
              paddingTop: convertPxToRem(10),
              paddingBottom: convertPxToRem(10),
              paddingLeft: convertPxToRem(20),
              paddingRight: convertPxToRem(20),
              minWidth: 100,
            }}
          >
            Add
          </Button>
        </Stack>

        {entriesAdded >= MAX_NEW_ENTRIES && (
          <Typography
            sx={{
              marginTop: 2,
              fontSize: 12,
              color: theme.palette.error.main,
              fontWeight: 600,
            }}
          >
            Maximum of {MAX_NEW_ENTRIES} new entries reached
          </Typography>
        )}
      </Box>

      <Box sx={{ padding: 2, marginBottom: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 2,
            color: theme.palette.text.primary,
          }}
        >
          Scoring Rules
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            border: `1px solid ${theme.palette.grey[300]}`,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                  Range (min/week)
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                  Category
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontSize: 12 }}>&gt;300</TableCell>
                <TableCell
                  sx={{
                    fontSize: 12,
                    color: themeColors.success,
                    fontWeight: 600,
                  }}
                >
                  GREEN (healthy)
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontSize: 12 }}>&gt;150</TableCell>
                <TableCell
                  sx={{
                    fontSize: 12,
                    color: themeColors.warning,
                    fontWeight: 600,
                  }}
                >
                  AMBER (low)
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontSize: 12 }}>&lt;150</TableCell>
                <TableCell
                  sx={{
                    fontSize: 12,
                    color: themeColors.error,
                    fontWeight: 600,
                  }}
                >
                  RED (inactive)
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
}
